/**
 * `rating` — stars (or hearts) drawn **in the cell**; there is no popover.
 *
 * Measured: three patient clicks on monday's rating cell opened nothing
 * (`editors/rating/tier2-capture.json`, "still no editor after three clicks"), and the
 * README's own description is *"Click a star; clicking the current value clears it"* — so the
 * type opts out of "second click edits" and paints an interactive row of glyphs.
 */
import { getColumnType, ratingLimit, type RatingSettings, type RatingValue } from '@elizmonday/shared';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { RatingSettingsDialog } from './SettingsDialog';

const shared = getColumnType('rating');

const GLYPHS: Readonly<Record<string, string>> = { stars: '★', hearts: '♥' };

export function ratingGlyph(settings: RatingSettings): string {
  return GLYPHS[settings.symbol ?? 'stars'] ?? '★';
}

export const ratingColumnUi = defineColumnUi<RatingValue, RatingSettings>({
  type: 'rating',
  icon: 'favorite',
  gallery: { group: 'numbers', keywords: ['rating', 'stars', 'score', 'hearts'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,
  /** No editor: one click sets the value, exactly like `checkbox` `[editors/README.md]`. */
  activateOnFirstClick: true,

  Cell: ({ value, settings, item, column }) => {
    const limit = ratingLimit(settings);
    const filled = value?.rating ?? 0;
    const glyph = ratingGlyph(settings);
    return (
      <span className="ez-rating" data-testid={`rating-${column.id}-${item.id}`} role="img" aria-label={`${filled} of ${limit}`}>
        {Array.from({ length: limit }, (_, index) => (
          <span
            key={index}
            className="ez-rating__glyph"
            data-filled={index < filled ? 'true' : 'false'}
            style={index < filled && settings.color ? { color: settings.color } : undefined}
            aria-hidden="true"
          >
            {glyph}
          </span>
        ))}
      </span>
    );
  },

  /**
   * The editor exists only so the grid has something to mount when the cell is activated: it
   * is the same row of glyphs, each one clickable, and clicking the current value clears it.
   */
  Editor: ({ value, settings, onCommit }) => {
    const limit = ratingLimit(settings);
    const filled = value?.rating ?? 0;
    const glyph = ratingGlyph(settings);
    return (
      <span className="ez-rating ez-rating--editing">
        {Array.from({ length: limit }, (_, index) => {
          const score = index + 1;
          return (
            <button
              key={score}
              type="button"
              className="ez-rating__button"
              data-filled={score <= filled ? 'true' : 'false'}
              data-testid={`rating-set-${String(score)}`}
              aria-label={`Rate ${String(score)}`}
              // Clicking the current value clears it (README).
              onClick={() => onCommit(score === filled ? null : { rating: score })}
            >
              {glyph}
            </button>
          );
        })}
      </span>
    );
  },

  SettingsDialog: RatingSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    return trimmed === '' ? null : (shared.parseValue(trimmed, settings) as RatingValue | null);
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('rating'),
});
